import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Box,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EventIcon from '@mui/icons-material/Event';
import api from '@/lib/api';
import { downloadFile } from '@/lib/download';
import { toast } from '@/lib/toast';
import { useAuth } from '@/lib/auth';
import { isOutsideBusinessHours } from '@/lib/businessHours';
import useIsMobile from '@/lib/useIsMobile';
import AppointmentChangelog from '@/components/AppointmentChangelog';
import { toLocalInputValue, fromLocalInputValue } from '@/components/calendar/dateUtils';

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'no_show', label: 'No-show' },
  { value: 'cancelled', label: 'Cancelled' },
];

const blankForm = {
  service_id: '',
  member_id: '',
  trainer_id: '',
  start_time: '',
  actual_duration_minutes: '',
  notes: '',
  status: 'scheduled',
};

const AppointmentDialog = ({
  open,
  mode = 'edit',
  appointment,
  initialStart,
  services = [],
  members = [],
  employees = [],
  businessHours = null,
  currentUserId,
  onClose,
  onSaved,
}) => {
  const theme = useTheme();
  const { isAdmin } = useAuth();
  const isMobile = useIsMobile();
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState('details');
  const showTabs = mode === 'edit' && isAdmin && !!appointment;

  useEffect(() => {
    if (open) setTab('details');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && appointment) {
      setForm({
        service_id: appointment.service_id || '',
        member_id: appointment.member_id || '',
        trainer_id: appointment.trainer_id || '',
        start_time: toLocalInputValue(appointment.start_time),
        actual_duration_minutes:
          appointment.actual_duration_minutes != null ? String(appointment.actual_duration_minutes) : '',
        notes: appointment.notes || '',
        status: appointment.status || 'scheduled',
      });
    } else {
      setForm({
        ...blankForm,
        trainer_id: currentUserId || '',
        start_time: initialStart ? toLocalInputValue(initialStart) : '',
      });
    }
  }, [open, mode, appointment, initialStart, currentUserId]);

  const employeeOptions = useMemo(() => {
    const opts = [...employees];
    if (form.trainer_id && !opts.find((e) => e.id === form.trainer_id)) {
      opts.unshift({ id: form.trainer_id, label: 'Current trainer' });
    }
    return opts;
  }, [employees, form.trainer_id]);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Non-blocking indication only — scheduling outside business hours is allowed.
  const outsideHours = useMemo(() => {
    const start = fromLocalInputValue(form.start_time);
    if (!start) return false;
    const svc = services.find((s) => s.service_id === form.service_id);
    const minutes = Number(form.actual_duration_minutes) || svc?.duration_minutes || 0;
    return isOutsideBusinessHours(businessHours, start, minutes);
  }, [form.start_time, form.service_id, form.actual_duration_minutes, services, businessHours]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const start = fromLocalInputValue(form.start_time);
    if (!start) {
      toast.error('Please choose a valid date and time');
      return;
    }
    setSubmitting(true);
    try {
      const actualParsed = form.actual_duration_minutes === '' ? null : Number(form.actual_duration_minutes);
      if (actualParsed !== null && (!Number.isFinite(actualParsed) || actualParsed <= 0)) {
        toast.error('Actual duration must be a positive number');
        return;
      }
      if (mode === 'edit' && appointment) {
        const payload = {
          service_id: form.service_id,
          member_id: form.member_id,
          trainer_id: form.trainer_id,
          start_time: start.toISOString(),
          actual_duration_minutes: actualParsed,
          notes: form.notes,
          status: form.status,
        };
        await api.put(`/appointments/${appointment.appointment_id}`, payload);
        toast.success('Appointment updated');
      } else {
        await api.post('/appointments', {
          service_id: form.service_id,
          member_id: form.member_id,
          trainer_id: form.trainer_id || currentUserId,
          start_time: start.toISOString(),
          actual_duration_minutes: actualParsed,
          notes: form.notes,
        });
        toast.success('Appointment booked');
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save appointment');
    } finally {
      setSubmitting(false);
    }
  };

  // Quick checkout: invoice the appointment's member for its service.
  const handleCreateInvoice = async () => {
    if (!appointment) return;
    setSubmitting(true);
    try {
      await api.post('/invoices', {
        member_id: appointment.member_id,
        items: [{ service_id: appointment.service_id, quantity: 1 }],
        appointment_id: appointment.appointment_id,
      });
      toast.success('Invoice created — open Billing to take payment');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadIcs = async () => {
    if (!appointment) return;
    try {
      await downloadFile(`/appointments/${appointment.appointment_id}/calendar.ics`, 'appointment.ics');
    } catch {
      toast.error('Failed to download calendar file');
    }
  };

  const handleDelete = async () => {
    if (!appointment) return;
    // eslint-disable-next-line no-alert
    if (typeof window !== 'undefined' && !window.confirm('Delete this appointment?')) return;
    setSubmitting(true);
    try {
      await api.delete(`/appointments/${appointment.appointment_id}`);
      toast.success('Appointment deleted');
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const dateInputSx = {
    '& input[type="datetime-local"]': {
      colorScheme: theme.palette.mode,
    },
    '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
      filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
      cursor: 'pointer',
      opacity: 0.7,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { bgcolor: 'background.paper' } }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: showTabs ? 0 : undefined }}>
          {mode === 'edit' ? 'Edit appointment' : 'Book appointment'}
        </DialogTitle>
        {showTabs && (
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="details" label="Details" />
            <Tab value="history" label="History" />
          </Tabs>
        )}
        <DialogContent>
          {tab === 'history' ? (
            <AppointmentChangelog appointmentId={appointment?.appointment_id} />
          ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              required
              label="Service"
              fullWidth
              value={form.service_id}
              onChange={handleChange('service_id')}
            >
              {services.map((svc) => (
                <MenuItem key={svc.service_id} value={svc.service_id}>
                  {svc.name} — {svc.duration_minutes}min — ${svc.price}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              required
              label="Member"
              fullWidth
              value={form.member_id}
              onChange={handleChange('member_id')}
            >
              {members.map((mem) => (
                <MenuItem key={mem.member_id} value={mem.member_id}>
                  {mem.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Assigned to"
              fullWidth
              value={form.trainer_id}
              onChange={handleChange('trainer_id')}
              helperText={employeeOptions.length === 0 ? 'No employees available — defaults to you' : ' '}
            >
              {employeeOptions.length === 0 && (
                <MenuItem value="">Me</MenuItem>
              )}
              {employeeOptions.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Date & time"
              type="datetime-local"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.start_time}
              onChange={handleChange('start_time')}
              sx={dateInputSx}
            />
            {outsideHours && (
              <Alert severity="warning" variant="outlined">
                Outside of normal business hours
              </Alert>
            )}
            <TextField
              label="Actual duration (min)"
              type="number"
              placeholder="Auto — uses service duration"
              fullWidth
              inputProps={{ min: 1, step: 1 }}
              value={form.actual_duration_minutes}
              onChange={handleChange('actual_duration_minutes')}
            />
            {mode === 'edit' && (
              <TextField
                select
                label="Status"
                fullWidth
                value={form.status}
                onChange={handleChange('status')}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={2}
              value={form.notes}
              onChange={handleChange('notes')}
            />
          </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {tab === 'history' ? (
            <Button onClick={onClose}>Close</Button>
          ) : (
            <>
              {showTabs && (
                <Box sx={{ mr: 'auto', display: 'flex', gap: 1 }}>
                  <Button color="error" onClick={handleDelete} disabled={submitting}>
                    Delete
                  </Button>
                  <Button onClick={handleCreateInvoice} disabled={submitting}>
                    Create invoice
                  </Button>
                  <Button startIcon={<EventIcon />} onClick={handleDownloadIcs} disabled={submitting}>
                    .ics
                  </Button>
                </Box>
              )}
              <Button onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {mode === 'edit' ? 'Save changes' : 'Book'}
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AppointmentDialog;
